import { useFaceRecognition } from "@/hooks/useFaceRecognition";
import { platformAssets } from "@/utils/assets";
import { Button, StaggerAnimation } from "@starsoft/common/components";
import { useSearchParams } from "@starsoft/common/hooks";
import { AnimatePresence, motion } from "framer-motion";
import useTranslation from "next-translate/useTranslation";
import { useMemo } from "react";
import styles from "./styles.module.scss";
import { getCircleVariants } from "./variants";

export default function FaceRecognition() {
  const { t } = useTranslation("common");
  const search: URLSearchParams = useSearchParams();
  const iframeUrl = search.get("url");

  const { url, error, retryUrl } = useFaceRecognition({
    iframeUrl: iframeUrl ?? undefined,
    fallbackSubdomain: "alternative",
    originalSubdomain: "main",
  });

  const Decorations = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, index) => (
        <motion.div
          className={styles.container__circle}
          variants={getCircleVariants(index)}
          initial="hidden"
          animate="visible"
          key={`circle-decoration-${index}`}
        />
      )),
    [],
  );

  if (error || !iframeUrl) {
    return (
      <div className={styles.container}>
        <AnimatePresence>{Decorations}</AnimatePresence>
        <StaggerAnimation
          direction="column"
          stagger={0.25}
          staggerDirection="down"
          className={styles.container__content}
        >
          <img
            width={300}
            height={100}
            src={platformAssets.logo.web.dark}
            className={styles.container__content__logo}
            alt="logo"
          />
          <h1 className={styles.container__content__heading}>
            {t("face_recognition_error_title")}
          </h1>
          <p className={styles.container__content__description}>
            {t("face_recognition_error_description")}
          </p>
          <Button onClick={() => window.open(retryUrl as string, "_self")}>
            {t("face_recognition_error_button")}
          </Button>
        </StaggerAnimation>
      </div>
    );
  }

  return (
    <iframe
      sandbox="allow-same-origin allow-forms allow-scripts"
      src={url as string}
      className={styles.iframe}
      style={{
        width: "100% !important",
        height: "100% !important",
        border: "none !important",
      }}
      allow="camera; microphone; geolocation; fullscreen; clipboard-read; clipboard-write; accelerometer; autoplay; gyroscope"
      allowFullScreen
      title="Face Recognition"
    />
  );
}
